import { isAxiosError } from 'axios';

/**
 * Il menu di react-select dentro una modal va reso in un portal sul body:
 * il corpo della Dialog ha overflow e lo ritaglierebbe (sembrerebbe che la
 * select non si apra). Stesso accorgimento di TeamSettingsModal/CustomFormV3.
 */
export const MENU_IN_PORTAL = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed' as const,
};

/** I sette giorni della griglia, da lunedì. */
export const DAYS = [
  { key: 'lun', short: 'Lun', label: 'Lunedì' },
  { key: 'mar', short: 'Mar', label: 'Martedì' },
  { key: 'mer', short: 'Mer', label: 'Mercoledì' },
  { key: 'gio', short: 'Gio', label: 'Giovedì' },
  { key: 'ven', short: 'Ven', label: 'Venerdì' },
  { key: 'sab', short: 'Sab', label: 'Sabato' },
  { key: 'dom', short: 'Dom', label: 'Domenica' },
] as const;

const MONTHS_SHORT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

/** Colore di ripiego se una categoria arrivasse senza il suo. */
export const FALLBACK_CATEGORY_COLOR = 'var(--c-text-3)';

/**
 * Limite del titolo/descrizione di un blocco.
 *
 * Rispecchia `TB_TITLE_MAX_LENGTH` in `apps/backend/src/enums/training-blocks.ts`,
 * che resta la fonte autorevole: lo applicano sia il DTO sia lo schema Mongoose.
 * Qui serve solo a fermare la digitazione prima della chiamata, invece di far
 * scoprire il limite con un 400 a form pieno. Se cambia là, cambia qui.
 */
export const TITLE_MAX_LENGTH = 200;

/** `YYYY-MM-DD` nel fuso locale — mai `toISOString`, che sposta di un giorno. */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Lunedì della settimana che contiene la data. Domenica appartiene alla settimana precedente. */
export function mondayOf(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d;
}

export function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/**
 * Messaggio in italiano per un errore di lettura (GET falliti).
 *
 * Il 429 del rate limiter ha un testo dedicato: "aspetta un attimo" è
 * un'indicazione concreta, un errore generico no. Copre sia il caricamento
 * della settimana sia quello della tassonomia, che falliscono allo stesso
 * modo e devono dire la stessa cosa a chi li vede.
 */
export function readErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.status === 429) {
    return 'Troppe richieste in questo momento. Aspetta qualche istante e riprova.';
  }
  return 'Non è stato possibile caricare i dati. Riprova.';
}

/**
 * Singolare/plurale italiano per un conteggio: "1 blocco" / "3 blocchi".
 * Le forme irregolari (se mai servissero) vanno passate esplicitamente,
 * questo helper si limita a scegliere fra le due stringhe fornite.
 */
export function pluralizeIt(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

/** "3 — 9 Ago 2026" */
export function weekRangeLabel(weekStart: Date): string {
  const end = addDays(weekStart, 6);
  const sameMonth = weekStart.getMonth() === end.getMonth();
  const left = sameMonth
    ? `${weekStart.getDate()}`
    : `${weekStart.getDate()} ${MONTHS_SHORT[weekStart.getMonth()]}`;
  return `${left} — ${end.getDate()} ${MONTHS_SHORT[end.getMonth()]} ${end.getFullYear()}`;
}
